import DeliveryIcon from '@assets/icons/delivery.svg';
import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil-thin.svg';
import PrinterIcon from '@assets/icons/printer.svg';
import React, { useState } from 'react';
import RedoIcon from '@assets/icons/redo.svg';
import TrashIcon from '@assets/icons/trash-small.svg';
import {
  Action,
  TagPrintBatchAction,
  TagPrintBatchState,
} from '@/hooks/use-tag-print-batch-reducer';
import { CombinedActionSheet, JumpToFn } from './combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { List } from './list';
import { ListOption } from './list-item';
import { PrintTagTemplateFieldsFragment } from '@/types';
import { SelectTagPrintBatchTemplate } from './tag-print-batch/select-tag-print-batch-template';
import { StyleProp, TextStyle } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { Switch } from './switch';
import { TagPrintBatchDeleteConfirmation } from './tag-print-batch-delete-confirmation';
import { TagPrintBatchRenameInput } from './tag-print-batch-rename-input';
import { TagPrintBatchTemplateUnavailableOffline } from './tag-print-batch-template-unavailable-offline';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';

export enum TagPrintBatchManagementRoute {
  ChangeTemplate = 'CHANGE_TEMPLATE',
  Delete = 'DELETE',
  InstantPrint = 'INSTANT_PRINT',
  Rename = 'RENAME',
  RequestDelivery = 'REQUEST_DELIVERY',
  Select = 'SELECT',
  Sort = 'SORT',
  TemplateUnavailableOffline = 'TEMPLATE_UNAVAILABLE_OFFLINE',
}

type TagPrintBatchManagementRouteOption = ListOption<{
  Icon: React.FC<SvgProps>;
  iconProps?: SvgProps;
  listTitleStyle?: StyleProp<TextStyle>;
  renderRightComponent?: (
    jumpTo: JumpToFn<TagPrintBatchManagementRoute>,
  ) => React.ReactNode;
}>;

interface Props {
  canUseSelectedPrintTemplateOffline: boolean;
  dispatch: React.Dispatch<Action>;
  initialRoute?: TagPrintBatchManagementRoute | null;
  isInstantPrint: boolean;
  onClose: () => void;
  onDelete: () => void;
  onRequestDeliveryOfTags: () => Promise<void>;
  printTagBatchUUID: string;
  setEnablingInstantPrint: React.Dispatch<React.SetStateAction<boolean>>;
  setIsInstantPrint: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPrinterSelection: React.Dispatch<React.SetStateAction<boolean>>;
  state: TagPrintBatchState;
}

export const TagPrintBatchManagementActionSheet: React.FC<Props> = ({
  canUseSelectedPrintTemplateOffline,
  dispatch,
  initialRoute,
  isInstantPrint,
  onClose,
  onDelete,
  onRequestDeliveryOfTags,
  printTagBatchUUID,
  setEnablingInstantPrint,
  setIsInstantPrint,
  setShowPrinterSelection,
  state,
}) => {
  const [isSelectingOfflineTemplate, setIsSelectingOfflineTemplate] =
    useState(false);
  const [styles, theme] = useStyles();
  const { selectedDevice } = useBluetoothContext();
  const isOfflineModeActive = useOfflineModeActive();
  const batchDeliveryEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.batch_delivery_enabled;

  const initialRouteOptions: TagPrintBatchManagementRouteOption[] = [
    {
      metadata: {
        Icon: PrinterIcon,
        iconProps: {
          stroke: theme.sk.color.icon.primary,
        },
        renderRightComponent: (jumpTo) => (
          <Switch
            onValueChange={() => {
              if (isInstantPrint) {
                setIsInstantPrint(false);
              } else {
                if (!selectedDevice) {
                  setEnablingInstantPrint(true);
                  if (
                    isOfflineModeActive &&
                    !canUseSelectedPrintTemplateOffline
                  ) {
                    setIsSelectingOfflineTemplate(true);
                    jumpTo(
                      TagPrintBatchManagementRoute.TemplateUnavailableOffline,
                    );
                    return;
                  } else {
                    setShowPrinterSelection(true);
                  }
                } else {
                  setIsInstantPrint(true);
                }
              }
              onClose();
            }}
            testID="instant-print-switch"
            trackingData={{
              name: 'tag_print_batch.instant_print_switch',
              properties: {
                instantPrintEnabled: !isInstantPrint,
                printTagBatchUUID,
              },
            }}
            value={isInstantPrint}
          />
        ),
      },
      title: i18n.t('instant_print'),
      value: TagPrintBatchManagementRoute.InstantPrint,
    },
    {
      metadata: {
        Icon: RedoIcon,
        iconProps: {
          stroke: theme.sk.color.icon.primary,
        },
      },
      title: i18n.t('change_template'),
      value: TagPrintBatchManagementRoute.ChangeTemplate,
    },
  ];

  if (batchDeliveryEnabled) {
    initialRouteOptions.push({
      metadata: {
        Icon: DeliveryIcon,
        iconProps: {
          stroke: isOfflineModeActive
            ? theme.sk.color.icon.disabled
            : theme.sk.color.icon.primary,
        },
        listTitleStyle: isOfflineModeActive ? styles.disabledTitle : undefined,
      },
      title: i18n.t('request_delivery_of_tags'),
      value: TagPrintBatchManagementRoute.RequestDelivery,
    });
  }

  const routeOptions: TagPrintBatchManagementRouteOption[] = [
    ...initialRouteOptions,
    {
      metadata: {
        Icon: PencilIcon,
        iconProps: {
          stroke: isOfflineModeActive
            ? theme.sk.color.icon.disabled
            : theme.sk.color.icon.primary,
        },
        listTitleStyle: isOfflineModeActive ? styles.disabledTitle : undefined,
      },
      title: i18n.t('rename_batch'),
      value: TagPrintBatchManagementRoute.Rename,
    },
    {
      metadata: {
        Icon: TrashIcon,
        iconProps: {
          stroke: isOfflineModeActive
            ? theme.sk.color.icon.disabled
            : theme.sk.color.icon.negative.primary,
        },
        listTitleStyle: isOfflineModeActive
          ? styles.disabledTitle
          : styles.deleteTitle,
      },
      title: i18n.t('delete_batch'),
      value: TagPrintBatchManagementRoute.Delete,
    },
  ];

  return (
    <CombinedActionSheet
      animateTransitions
      initialRoute={initialRoute ?? TagPrintBatchManagementRoute.Select}
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case TagPrintBatchManagementRoute.ChangeTemplate:
            return (
              <SelectTagPrintBatchTemplate
                initialTemplate={
                  initialRoute ===
                  TagPrintBatchManagementRoute.TemplateUnavailableOffline
                    ? null
                    : state.template
                }
                onSelectTemplate={async (
                  template: PrintTagTemplateFieldsFragment | null,
                ) => {
                  if (template) {
                    dispatch({
                      payload: { template },
                      type: TagPrintBatchAction.UpdateTemplate,
                    });
                    if (
                      initialRoute ===
                        TagPrintBatchManagementRoute.TemplateUnavailableOffline ||
                      isSelectingOfflineTemplate
                    ) {
                      /*
                       * we came from a "template unavailable offline state"
                       * OR when enabling instant print without offline-supported template
                       * */
                      setShowPrinterSelection(true);
                    }
                  }
                  setIsSelectingOfflineTemplate(false);
                  onClose();
                }}
              />
            );
          case TagPrintBatchManagementRoute.Delete:
            return (
              <TagPrintBatchDeleteConfirmation
                onClose={onClose}
                onDelete={onDelete}
                printTagBatchUUID={printTagBatchUUID}
              />
            );
          case TagPrintBatchManagementRoute.Rename:
            return (
              <TagPrintBatchRenameInput
                initialBatchName={state.name}
                onClose={(name) => {
                  dispatch({
                    payload: { name },
                    type: TagPrintBatchAction.RenameBatch,
                  });
                  onClose();
                }}
              />
            );
          case TagPrintBatchManagementRoute.TemplateUnavailableOffline:
            return (
              <TagPrintBatchTemplateUnavailableOffline
                onAction={() => {
                  jumpTo(TagPrintBatchManagementRoute.ChangeTemplate);
                }}
              />
            );
          case TagPrintBatchManagementRoute.Select:
            return (
              <List
                data={routeOptions}
                listItemTitleStyle={(item) => {
                  const { listTitleStyle } = item.metadata!;
                  return listTitleStyle;
                }}
                onSelect={(item) => {
                  if (isOfflineModeActive) {
                    switch (item.value) {
                      case TagPrintBatchManagementRoute.Rename:
                      case TagPrintBatchManagementRoute.Delete:
                      case TagPrintBatchManagementRoute.RequestDelivery:
                      case TagPrintBatchManagementRoute.InstantPrint:
                        break;
                      default:
                        jumpTo(item.value as TagPrintBatchManagementRoute);
                    }
                  } else {
                    switch (item.value) {
                      case TagPrintBatchManagementRoute.RequestDelivery:
                        onRequestDeliveryOfTags();
                        break;
                      case TagPrintBatchManagementRoute.InstantPrint:
                        break;
                      default:
                        jumpTo(item.value as TagPrintBatchManagementRoute);
                    }
                  }
                }}
                renderLeftComponent={(item) => {
                  const { Icon, iconProps } = item.metadata!;
                  return (
                    <Icon
                      height={24}
                      style={styles.icon}
                      width={24}
                      {...iconProps}
                    />
                  );
                }}
                renderRightComponent={(item) => {
                  const { renderRightComponent } = item.metadata!;
                  return renderRightComponent
                    ? renderRightComponent(jumpTo)
                    : null;
                }}
              />
            );
          default:
            return null;
        }
      }}
      routes={{
        [TagPrintBatchManagementRoute.ChangeTemplate]: {
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.InstantPrint]: {
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.Delete]: {
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.Rename]: {
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.RequestDelivery]: {
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.Select]: {
          contentStyle: styles.selectList,
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.Sort]: {
          previousKey: null,
        },
        [TagPrintBatchManagementRoute.TemplateUnavailableOffline]: {
          previousKey: null,
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  deleteTitle: {
    color: theme.sk.color.text.error,
  },
  disabledTitle: {
    color: theme.sk.color.text.disabled,
  },
  icon: {
    marginRight: 8,
  },
  selectList: {
    paddingHorizontal: 8,
  },
}));
